import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import config from '../config';
import { UserContext } from '../contexts/UserContext';
import LoadingDots from '../components/LoadingDots';
import './DeleteAccount.css';

const DeleteAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const tokenFromUrl = searchParams.get('token');

  const [formData, setFormData] = useState({
    reason: '',
    details: '',
    password: '',
    confirm: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [preview, setPreview] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [loadingCounts, setLoadingCounts] = useState(true);

  // Check authentication and redirect to login if not authenticated
  useEffect(() => {
    if (!tokenFromUrl) {
      // Only check auth if not on confirmation page
      const token = Cookies.get('authToken');
      if (!token && !user) {
        // Redirect to login page with return path
        navigate('/login', { state: { from: '/delete-account' } });
        return;
      }
      setAuthChecking(false);
    } else {
      // On confirmation page, skip auth check
      setAuthChecking(false);
    }
  }, [user, tokenFromUrl, navigate]);

  // Load preview on component mount if user is logged in
  useEffect(() => {
    const token = Cookies.get('authToken');
    if (token) {
      loadPreview(token);
    }
  }, []);

  const loadPreview = async (token) => {
    setLoadingCounts(true);
    try {
      const response = await axios.get(`${config.backendUrl}/users/account/deletion-preview/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        }
      });
      setPreview(response.data.data);
    } catch (err) {
      console.error('Error loading preview:', err);
    } finally {
      setLoadingCounts(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = Cookies.get('authToken');
      
      if (!token) {
        setError('Vous devez être connecté pour effectuer cette action');
        setLoading(false);
        return;
      }

      // Validate password
      if (!formData.password || formData.password.trim().length < 6) {
        setError('Veuillez entrer un mot de passe valide (minimum 6 caractères)');
        setLoading(false);
        return;
      }

      // Request SMS verification code
      const response = await axios.post(
        `${config.backendUrl}/users/account/request-deletion/`,
        {
          password: formData.password,
          reason: `${formData.reason} - ${formData.details}`.trim()
        },
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      // Navigate to verification page
      navigate('/delete-account-verification', {
        state: { 
          maskedPhone: response.data.masked_phone,
          reason: formData.reason,
          password: formData.password
        }
      });

    } catch (err) {
      console.error('Delete account request error:', err.response?.data);
      
      let errorMessage = 'Erreur lors de l\'envoi du code de vérification';
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
        
        // If the error is about missing phone number, provide helpful guidance
        if (errorMessage.includes('numéro de téléphone') || errorMessage.includes('mobile')) {
          errorMessage += ' Vous pouvez ajouter votre numéro dans la section "Paramètres du compte".';
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 400) {
        errorMessage = 'Vérifiez vos informations et réessayez';
      } else if (err.response?.status === 401) {
        errorMessage = 'Mot de passe incorrect';
      } else if (err.response?.status === 500) {
        errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
      }
      
      // Show additional details in console for debugging
      if (err.response?.data?.details) {
        console.error('Error details:', err.response.data.details);
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleConfirmDeletion = async () => {
    if (!tokenFromUrl) {
      setError('Token de confirmation manquant');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(
        `${config.API_BASE_URL}/users/account/confirm-deletion/`,
        {
          token: tokenFromUrl,
          password: formData.password
        },
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      setSuccess(true);
      setLoading(false);

      // Redirect to home after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la confirmation');
      setLoading(false);
    }
  };

  // Show skeleton while checking authentication
  if (authChecking) {
    return (
      <div className="delete-account-container">
        <div className="delete-account-card">
          <div className="skeleton-container">
            <div className="skeleton skeleton-title"></div>
            <div className="skeleton skeleton-text"></div>
            <div className="skeleton skeleton-text short"></div>
            <div style={{ marginTop: '24px' }}>
              <div className="skeleton skeleton-box"></div>
              <div className="skeleton skeleton-box"></div>
              <div className="skeleton skeleton-box"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (tokenFromUrl) {
    // Confirmation page
    return (
      <div className="delete-account-container">
        <div className="delete-account-card">
          <div className="logo-delete">
            <h1>🗑️ Wennze</h1>
          </div>

          {success ? (
            <div className="success-message">
              <h2>✅ Compte supprimé</h2>
              <p>Votre compte a été supprimé avec succès. Redirection en cours...</p>
            </div>
          ) : (
            <>
              <div className="warning-box">
                <h2>⚠️ Confirmation finale</h2>
                <p>Entrez votre mot de passe pour confirmer la suppression définitive de votre compte.</p>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="form-section">
                <div className="input-group">
                  <label htmlFor="password">Mot de passe</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                  />
                </div>

                <div className="button-group">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate('/')}
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleConfirmDeletion}
                    disabled={loading || !formData.password}
                  >
                    {loading ? 'Suppression...' : 'Confirmer la suppression'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Request deletion page
  return (
    <div className="delete-account-container">
      <div className="delete-account-card">
        <div className="logo-delete">
          <h1>🗑️ Wennze</h1>
        </div>

        {success ? (
          <div className="success-message">
            <h2>✅ Code envoyé</h2>
            <p>Un code de vérification a été envoyé à votre numéro de téléphone. Vous allez être redirigé vers la page de vérification...</p>
          </div>
        ) : (
          <>
            {error && <div className="error-message">{error}</div>}

            {loading && (
              <div className="skeleton-container">
                <div className="skeleton skeleton-box"></div>
                <div className="skeleton skeleton-text"></div>
                <div className="skeleton skeleton-text short"></div>
                <div style={{ marginTop: '20px' }}>
                  <div className="skeleton skeleton-input"></div>
                  <div className="skeleton skeleton-input"></div>
                </div>
              </div>
            )}

            {!loading && (
              <>
                <div className="warning-box">
                  <h2>⚠️ Attention: Cette action est irréversible</h2>
                  <p><strong>Cette action est définitive et toutes vos données seront supprimées de manière permanente.</strong></p>
                </div>

                {/* Data Preview */}
                <div className="preview-data">
                  <h3>📊 Données qui seront supprimées</h3>
                  
                  <div className="preview-item">
                    <div className="preview-item-left">
                      <div className="preview-icon-container icon-products">📦</div>
                      <span className="preview-item-label">Produits</span>
                    </div>
                    {loadingCounts ? <LoadingDots size={4} /> : <span className="preview-item-count">{preview?.data_to_delete?.products || 0}</span>}
                  </div>

                  <div className="preview-item">
                    <div className="preview-item-left">
                      <div className="preview-icon-container icon-messages">💬</div>
                      <span className="preview-item-label">Messages envoyés</span>
                    </div>
                    {loadingCounts ? <LoadingDots size={4} /> : <span className="preview-item-count">{preview?.data_to_delete?.messages_sent || 0}</span>}
                  </div>

                  <div className="preview-item">
                    <div className="preview-item-left">
                      <div className="preview-icon-container icon-conversations">💭</div>
                      <span className="preview-item-label">Conversations</span>
                    </div>
                    {loadingCounts ? <LoadingDots size={4} /> : <span className="preview-item-count">{preview?.data_to_delete?.conversations || 0}</span>}
                  </div>

                  <div className="preview-item">
                    <div className="preview-item-left">
                      <div className="preview-icon-container icon-campaigns">📢</div>
                      <span className="preview-item-label">Campagnes</span>
                    </div>
                    {loadingCounts ? <LoadingDots size={4} /> : <span className="preview-item-count">{preview?.data_to_delete?.campaigns || 0}</span>}
                  </div>

                  <div className="preview-item">
                    <div className="preview-item-left">
                      <div className="preview-icon-container icon-transactions">💳</div>
                      <span className="preview-item-label">Transactions</span>
                    </div>
                    {loadingCounts ? <LoadingDots size={4} /> : <span className="preview-item-count">{preview?.data_to_delete?.transactions || 0}</span>}
                  </div>

                  <div className="preview-item">
                    <div className="preview-item-left">
                      <div className="preview-icon-container icon-notifications">🔔</div>
                      <span className="preview-item-label">Notifications</span>
                    </div>
                    {loadingCounts ? <LoadingDots size={4} /> : <span className="preview-item-count">{preview?.data_to_delete?.notifications || 0}</span>}
                  </div>

                  <div className="preview-item">
                    <div className="preview-item-left">
                      <div className="preview-icon-container icon-wallet">👛</div>
                      <span className="preview-item-label">Solde Zaires</span>
                    </div>
                    {loadingCounts ? <LoadingDots size={4} /> : <span className="preview-item-count warning">{preview?.data_to_delete?.wallet_balance || 0} Z</span>}
                  </div>

                  <div className="preview-item">
                    <div className="preview-item-left">
                      <div className="preview-icon-container icon-followers">👥</div>
                      <span className="preview-item-label">Abonnés</span>
                    </div>
                    {loadingCounts ? <LoadingDots size={4} /> : <span className="preview-item-count">{preview?.data_to_delete?.followers || 0}</span>}
                  </div>

                  <div className="preview-item">
                    <div className="preview-item-left">
                      <div className="preview-icon-container icon-following">👤</div>
                      <span className="preview-item-label">Abonnements</span>
                    </div>
                    {loadingCounts ? <LoadingDots size={4} /> : <span className="preview-item-count">{preview?.data_to_delete?.following || 0}</span>}
                  </div>

                  <div className="preview-item">
                    <div className="preview-item-left">
                      <div className="preview-icon-container icon-ratings">⭐</div>
                      <span className="preview-item-label">Avis reçus</span>
                    </div>
                    {loadingCounts ? <LoadingDots size={4} /> : <span className="preview-item-count">{preview?.data_to_delete?.reviews_received || 0}</span>}
                  </div>

                  <div className="preview-item">
                    <div className="preview-item-left">
                      <div className="preview-icon-container icon-reviews-given">✍️</div>
                      <span className="preview-item-label">Avis donnés</span>
                    </div>
                    {loadingCounts ? <LoadingDots size={4} /> : <span className="preview-item-count">{preview?.data_to_delete?.reviews_given || 0}</span>}
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-section">
                    <h3>Pourquoi voulez-vous supprimer votre compte ?</h3>
                    <div className="input-group">
                      <select
                        id="reason"
                        name="reason"
                        value={formData.reason}
                        onChange={handleChange}
                        required
                      >
                        <option value="">-- Sélectionnez une raison --</option>
                        <option value="not_using">Je n'utilise plus l'application</option>
                        <option value="privacy_concerns">Préoccupations concernant la confidentialité</option>
                        <option value="too_many_notifications">Trop de notifications</option>
                        <option value="found_alternative">J'ai trouvé une alternative</option>
                        <option value="technical_issues">Problèmes techniques</option>
                        <option value="other">Autre raison</option>
                      </select>
                    </div>

                    <div className="input-group">
                      <label htmlFor="details">Détails supplémentaires (optionnel)</label>
                      <textarea
                        id="details"
                        name="details"
                        value={formData.details}
                        onChange={handleChange}
                        placeholder="Partagez-nous plus d'informations..."
                      />
                    </div>
                  </div>

                  <div className="form-section">
                    <h3>Confirmation</h3>
                    
                    <div className="input-group">
                      <label htmlFor="password">Mot de passe <span style={{ color: '#ff4757' }}>*</span></label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Entrez votre mot de passe pour confirmer"
                        required
                        minLength={6}
                      />
                      <small style={{ color: '#636e72', fontSize: '13px' }}>
                        Entrez votre mot de passe actuel pour confirmer la suppression
                      </small>
                    </div>

                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        id="confirm"
                        name="confirm"
                        checked={formData.confirm}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor="confirm">
                        Je comprends que cette action est définitive et que toutes mes données seront supprimées de manière permanente.
                      </label>
                    </div>
                  </div>

                  <div className="button-group">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate('/')}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn btn-danger"
                      disabled={!formData.confirm || !formData.password || formData.password.trim().length < 6}
                    >
                      Supprimer mon compte
                    </button>
                  </div>
                </form>

                <div className="info-box">
                  <p>
                    <strong>💡 Vous voulez juste faire une pause ?</strong><br />
                    Au lieu de supprimer votre compte, vous pouvez désactiver les notifications dans les paramètres de l'application ou simplement vous déconnecter.
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DeleteAccount;
